import { Container } from '@components/gassan-ui'
import { CleanLink } from '@components/gassan-ui/Link/CleanLink'
import { Icon } from '@components/old-rolex-ui'
import { Text } from '@components/rolex-ui/Text'
import { cn } from '@lib/cn'
import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock'
import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useEffect, useRef, useState } from 'react'

const Header: React.FC = () => {
  const router = useRouter()
  const ref = useRef()
  const [isMobileActive, setIsMobileActive] = useState(false)
  const {
    t,
    i18n: { language },
  } = useTranslation('rolex')

  const disableMobileMenu = () => setIsMobileActive(false)
  const toggleMobileMenu = () => setIsMobileActive(!isMobileActive)
  const rolexRootRoute = language === 'nl' ? '/rolex' : `/${language}/rolex`

  const navItems: { label: string; href: string; as?: string }[] = [
    {
      label: t('navigation.atGassan'),
      href: rolexRootRoute,
    },
    {
      label: t('navigation.watches'),
      href: `${rolexRootRoute}${t('rolex.watches', { ns: 'navigation' })}`,
    },
    {
      label: t('navigation.newWatches2025'),
      href: `${rolexRootRoute}${t('rolex.newWatches2025', { ns: 'navigation' })}`,
    },
    {
      label: t('navigation.watchmaking'),
      href: `${rolexRootRoute}${t('rolex.watchmaking', { ns: 'navigation' })}`,
    },
    {
      label: t('navigation.servicing'),
      href: `${rolexRootRoute}${t('rolex.serviceRolex', { ns: 'navigation' })}`,
    },
    {
      label: t('navigation.worldOfRolex'),
      href: `${rolexRootRoute}${t('rolex.worldOfRolex', { ns: 'navigation' })}`,
    },
    {
      label: t('navigation.contactUs'),
      href: `${rolexRootRoute}/contact`,
    },
  ]

  useEffect(() => {
    if (isMobileActive) {
      ref?.current && disableBodyScroll(ref.current)
    } else {
      ref?.current && enableBodyScroll(ref.current)
    }
  }, [isMobileActive])

  useEffect(() => {
    disableMobileMenu()
  }, [router.asPath])

  return (
    <div className="relative z-[3] flex h-20 items-center bg-gradient-to-r from-rolexGreenDark to-rolexPrimary md:h-[100px] lg:h-[110px]">
      <Container className="flex items-center justify-between">
        <CleanLink href={language === 'nl' ? '/rolex' : `/${language}/rolex`} className="flex">
          <Image
            className="m-0"
            src="/images/rolex/rolex_retailer_plaque.jpg"
            alt="Rolex retailer plaque"
            width={120}
            height={60}
          />
        </CleanLink>
        <nav className="rolex-desktop-nav w-full justify-end gap-6">
          {navItems.map((item, key) => (
            <Link href={item.href} key={key} type="link">
              <a>
                <Text
                  variant="fixed16"
                  className={cn(
                    'text-white transition-colors duration-100 hover:text-rolexOceanGreen',
                    item.href === `/${language}${router.asPath}` && 'text-rolexOceanGreen',
                  )}
                >
                  {item.label}
                </Text>
              </a>
            </Link>
          ))}
        </nav>
        <button
          onClick={() => toggleMobileMenu()}
          className="rolex-mobile-nav items-center gap-1 border-none text-white outline-none"
        >
          <Text variant="fixed16">Menu</Text>
          <div className={cn('transition-transform duration-100', isMobileActive && 'rotate-180')}>
            <Icon icon="rolex-mobile-menu-arrow" />
          </div>
        </button>
      </Container>
      <div
        className={cn(
          'pointer-events-none absolute top-20 h-[calc(100dvh-2rem)] w-full -translate-y-2 bg-transparent bg-gradient-to-r from-rolexGreenDark to-rolexPrimary py-8 opacity-0 transition-all duration-150 lg:hidden',
          isMobileActive && 'pointer-events-auto translate-y-0 opacity-100',
        )}
      >
        <Container as="nav" className="flex flex-col gap-8" ref={ref as any}>
          {navItems.map((item, key) => (
            <Link href={item.href} key={key} type="link">
              <a>
                <Text
                  variant="fixed16"
                  className={cn(
                    'text-white transition-colors duration-100',
                    item.href === `/${language}${router.asPath}` && 'text-rolexOceanGreen',
                  )}
                >
                  {item.label}
                </Text>
              </a>
            </Link>
          ))}
        </Container>
      </div>
    </div>
  )
}

export default Header
